/**
 * Editor surface. Given the current pointer, build a view and render it.
 * Objects render their fields inline if primitive, or as zoom rows if nested.
 * Arrays render a list of rows. Leaves render inline editors.
 */

import {
  appendPointer,
  buildView,
  defaultValueFor,
  getAtPointer,
  parentPointer,
  resolveSchema,
  schemaAtPointer,
  setAtPointer,
  type ArrayView,
  type FieldDescriptor,
  type JsonPointer,
  type ObjectFieldGroup,
  type ObjectView,
  type OneOfView,
  type ViewNode,
} from '@bliksys/schema-engine';
import type { JsonSchema, JsonValue } from '@bliksys/shared';
import { useEffect, useId, useMemo, useRef, useState, createContext, useContext } from 'react';

import { useShortcuts, modKey } from '../hooks/useShortcuts.js';
import { useAppState, useStore } from '../hooks/useStore.js';
import { APPLY_BLOCKED_TOAST } from '../messages.js';

import { AddFieldDialog } from './AddFieldDialog.js';
import { Breadcrumb } from './Breadcrumb.js';
import { confirm, confirmThreeWay } from './ConfirmDialog.js';
import { DataTable } from './DataTable.js';
import { FieldLabel } from './FieldLabel.js';
import { Icon } from './Icon.js';
import { BooleanInput, EnumInput, NumberInput, StringInput } from './LeafEditors.js';
import { toast } from './Toast.js';

import './Editor.css';

/**
 * Tracks how deeply we're already inline-expanded inside a single
 * top-level view. The renderer's dispatch consults this and falls back
 * to a zoom-row when depth exceeds `MAX_INLINE_DEPTH`. Without this
 * limit, recursive schemas could nest forever in place — visually
 * overwhelming and slow to render.
 *
 * The contract: schemas that opt into inline rendering via
 * `x-bliksys.layout: 'inline'` get expanded in place up to N levels
 * deep. Beyond that, the renderer falls back to zoom-row navigation.
 *
 * The current limit (3) covers realistic content schemas like the
 * demo's `pages → blocks → content (columns) → content (blocks)`
 * chain without runaway recursion. Recursive schemas (Block contains
 * ColumnsBlock contains Column contains Block) hit the limit and
 * gracefully degrade to zoom-rows past that point.
 */
const MAX_INLINE_DEPTH = 3;
const InlineDepthContext = createContext<number>(0);

/**
 * Guarded "zoom into" function provided by the Editor root and used by
 * any row-click handler in the tree. Encapsulates two behaviors:
 *
 *  1. When no draft is active, beginning a fresh edit draft for the
 *     target — so the user gets Apply/Discard semantics for editing
 *     existing items, matching the create flow.
 *  2. When a draft IS active and the target is OUTSIDE the draft
 *     sub-tree, prompting the user (Apply / Discard / Cancel) before
 *     letting them leave.
 *
 * Children call `zoomInto(pointer)` instead of `store.navigate(pointer)`
 * directly. The default no-op (used outside of an Editor) just routes
 * to plain navigate so test setups and isolated rendering still work.
 */
const ZoomContext = createContext<(pointer: string) => void>(() => {
  // No-op default for components rendered outside the Editor's
  // provider. Falls back to a normal navigate-via-store, but components
  // outside the Editor don't typically have store access at module
  // level either, so this is mostly a safety net.
});

export function Editor(): JSX.Element {
  const state = useAppState();
  const store = useStore();
  const view = useMemo(
    () =>
      buildView(state.pointer, {
        rootSchema: state.schema,
        rootData: state.data,
        refRegistry: state.refRegistry,
      }),
    [state.pointer, state.schema, state.data, state.refRegistry],
  );
  // When the user has zoomed into a sub-record, surface the immediate
  // parent's summary above the title so they don't lose track of which
  // record they're inside. The breadcrumb covers the *path*, but a
  // single-line "Inside …" tag matches what people scan for. Skipped
  // at the root since there's no parent.
  // Back button target — pointer + label. Shared between the Back
  // button rendering and click-handler so the label and destination
  // are guaranteed to match.
  const backTarget = useMemo(
    () =>
      state.pointer === '' ? null : computeBackTarget(state.pointer, state.schema, state.data),
    [state.pointer, state.schema, state.data],
  );

  // Reset scroll when navigating to a new pointer. Without this, zooming
  // into a deep record leaves the scroll position from the previous view
  // — confusing because the new view's content starts at the top of the
  // page but the user is staring at whatever was at the bottom of the
  // prior one. The scroll container is `.app-main`; we walk up from the
  // editor root rather than holding a ref into AppShell.
  useEffect(() => {
    const main = document.querySelector('.app-main');
    if (main instanceof HTMLElement) {
      main.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [state.pointer]);

  // Compute whether the current pointer is inside a draft sub-tree.
  // Used to surface Apply / Discard buttons. The draft flag covers the
  // full sub-tree — any pointer at-or-below the draft pointer is "inside
  // the draft", so editing nested fields doesn't lose the draft state.
  const isInsideDraft =
    state.draftPointer !== null &&
    (state.pointer === state.draftPointer || state.pointer.startsWith(`${state.draftPointer}/`));

  // Apply is gated on validity within the draft sub-tree. Computed
  // here so the button can show disabled state with a tooltip.
  const draftBlockingIssue = useMemo(() => {
    if (state.draftPointer === null) return null;
    const dotted = pointerToPath(state.draftPointer);
    return state.issues.find(
      (i) =>
        i.path === dotted || i.path.startsWith(`${dotted}.`) || i.path.startsWith(`${dotted}[`),
    );
  }, [state.draftPointer, state.issues]);

  const handleApply = (): void => {
    const ok = store.applyDraft();
    if (!ok && draftBlockingIssue) {
      // The store refused — surface the reason via toast so the user
      // understands why the button didn't dismiss the editor. Without
      // this, a click-and-nothing-happens leaves the user puzzled.
      toast(`Can't apply yet: ${draftBlockingIssue.message}`, 'error');
      return;
    }
    if (!ok) return;
    // applyDraft auto-navigates to parentPointer(draftPointer). If that
    // pointer is an inline-expanded ancestor (hidden in the breadcrumb),
    // walk further up so we land somewhere the user could've reached.
    const after = store.getState();
    const visible = visiblePointer(after.pointer, after.schema, after.data);
    if (visible !== after.pointer) store.navigate(visible);
  };

  const handleDiscard = (): void => {
    const isCreate = state.draftKind === 'create';
    void confirm({
      title: isCreate
        ? `Discard new ${view.label.toLowerCase()}?`
        : `Discard changes to ${view.label.toLowerCase()}?`,
      message: isCreate
        ? "The item will be removed and you'll go back to the list."
        : "Your edits will be reverted and you'll go back.",
      confirmLabel: 'Discard',
      danger: true,
    }).then((ok) => {
      if (!ok) return;
      store.discardDraft();
      // Same reasoning as handleApply — discardDraft auto-navigates to
      // parentPointer(draftPointer); skip past hidden ancestors.
      const after = store.getState();
      const visible = visiblePointer(after.pointer, after.schema, after.data);
      if (visible !== after.pointer) store.navigate(visible);
    });
  };

  // Mod+Enter / Mod+Shift+Backspace mirror the visible Apply / Discard
  // buttons. Both fire from anywhere inside the draft sub-tree, not just
  // the draft root, because keyboard users may be deep in a leaf field
  // when they decide to commit. When there's no draft, the shortcuts
  // simply do nothing — they don't conflict with text-input behavior
  // because Mod+Enter is rarely used for line-breaks in <input>/<textarea>.
  useShortcuts([
    {
      combo: 'mod+enter',
      allowInInputs: true,
      handler: () => {
        if (!isInsideDraft) return;
        if (!state.draftHasChanges) return;
        if (draftBlockingIssue) {
          toast(`Can't apply yet: ${draftBlockingIssue.message}`, 'error');
          return;
        }
        (document.activeElement as HTMLElement | null)?.blur?.();
        const ok = store.applyDraft();
        if (!ok) {
          toast(APPLY_BLOCKED_TOAST, 'error');
        }
      },
    },
    {
      combo: 'mod+shift+backspace',
      allowInInputs: true,
      handler: () => {
        if (!isInsideDraft) return;
        if (!state.draftHasChanges) return;
        handleDiscard();
      },
    },
  ]);

  // The Back button always tries to go to `backTarget.pointer` — the
  // ancestor whose label is shown. If that target is inside the draft
  // sub-tree (or there's no draft), navigate freely. If the target is
  // outside an active draft, show a blocking three-way prompt:
  // Apply / Discard / Cancel. After Apply or Discard resolves, finish
  // the navigation to the user's chosen destination — applyDraft's
  // built-in zoom-out is to the draft's parent, but the user asked to
  // go further (e.g. Back to Site, not Back to Pages), so we navigate
  // again on top.
  const showBack = backTarget !== null;
  const handleBack = (): void => {
    if (backTarget === null) return;
    const target = backTarget.pointer;
    if (state.draftPointer === null || !store.navigationLeavesDraft(target)) {
      // Free navigation — no draft, or target is still inside the draft.
      store.navigate(target);
      return;
    }
    // Target leaves the draft sub-tree. If the user hasn't actually
    // touched the draft (just zoomed in to look), silently clear the
    // draft state and navigate — no prompt for a no-op resolution.
    if (!store.draftHasChanges()) {
      store.clearDraft();
      store.navigate(target);
      return;
    }
    // Real edits in flight — block on the 3-way prompt.
    void confirmLeaveDraft(state.draftKind, view.label).then((action) => {
      if (action === 'cancel') return;
      if (action === 'apply') {
        const ok = store.applyDraft();
        if (!ok && draftBlockingIssue) {
          toast(`Can't apply yet: ${draftBlockingIssue.message}`, 'error');
          return;
        }
        if (store.getState().pointer !== target) store.navigate(target);
      } else {
        // 'discard'
        store.discardDraft();
        if (store.getState().pointer !== target) store.navigate(target);
      }
    });
  };

  // Guarded zoom for row clicks. Four branches:
  //  - No draft active: begin an edit draft at the target. Apply/Discard
  //    affordances appear when the user is at-or-inside the target.
  //    This is what gives existing items the same paradigm as new ones.
  //  - Draft active and target IS within the draft sub-tree: just
  //    navigate (the user is going deeper into the same edit context).
  //  - Draft active, target leaves draft, but no changes were made:
  //    silently clear the draft and start a fresh one at the target.
  //    No prompt — there's nothing to lose.
  //  - Draft active, target leaves draft, real edits exist: prompt
  //    Apply / Discard / Cancel.
  const zoomInto = (target: string): void => {
    if (state.draftPointer === null) {
      store.beginDraft(target, 'edit');
      store.navigate(target);
      return;
    }
    if (!store.navigationLeavesDraft(target)) {
      store.navigate(target);
      return;
    }
    if (!store.draftHasChanges()) {
      // No real edits in the draft we're leaving — just clear it and
      // start fresh. No prompt; the user wouldn't think they had
      // anything to apply or discard.
      store.clearDraft();
      store.beginDraft(target, 'edit');
      store.navigate(target);
      return;
    }
    void confirmLeaveDraft(state.draftKind, view.label).then((action) => {
      if (action === 'apply') {
        const ok = store.applyDraft();
        if (!ok && draftBlockingIssue) {
          toast(`Can't apply yet: ${draftBlockingIssue.message}`, 'error');
          return;
        }
        // After Apply, draft is clear — start a fresh edit draft at
        // the new target so the user gets the same Apply/Discard
        // affordance there too.
        store.beginDraft(target, 'edit');
        store.navigate(target);
      } else if (action === 'discard') {
        store.discardDraft();
        store.beginDraft(target, 'edit');
        store.navigate(target);
      }
      // 'cancel' → do nothing
    });
  };

  return (
    <ZoomContext.Provider value={zoomInto}>
      <div className="editor">
        <header className="editor-header">
          {/* Nav row only renders when we're not at the root: at the
              project home the breadcrumb has nothing to show besides
              the project itself (already named in the app bar) and
              the back button has no destination. */}
          {state.pointer !== '' && (
            <div className="editor-nav-row">
              {showBack && (
                <button
                  type="button"
                  className="editor-back"
                  onClick={handleBack}
                  aria-label={backTarget?.label ? `Back to ${backTarget.label}` : 'Back'}
                  title={backTarget?.label ? `Back to ${backTarget.label}` : 'Back'}
                >
                  <Icon name="arrow-left" size={16} strokeWidth={2.2} />
                </button>
              )}
              <Breadcrumb />
            </div>
          )}
          <div className="editor-title-row">
            <h1 className="editor-title">{view.label}</h1>
            {/* Apply/Discard live on every view inside the draft sub-tree,
                not just at the draft root, so the keyboard user (or
                anyone deep in a nested field) can commit without
                navigating back up first. The keyboard shortcuts already
                fire from anywhere inside the draft — the buttons now
                match. */}
            {isInsideDraft && (
              <div className="editor-draft-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleDiscard}
                  disabled={!state.draftHasChanges}
                  title={
                    !state.draftHasChanges
                      ? 'Nothing to discard yet'
                      : `Discard (${modKey()}+Shift+⌫)`
                  }
                  aria-keyshortcuts={`${modKey()}+Shift+Backspace`}
                >
                  Discard
                  <kbd className="editor-draft-kbd">{modKey()} ⇧ ⌫</kbd>
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleApply}
                  disabled={
                    !state.draftHasChanges ||
                    (draftBlockingIssue !== undefined && draftBlockingIssue !== null)
                  }
                  title={
                    !state.draftHasChanges
                      ? 'Nothing to apply yet'
                      : draftBlockingIssue
                        ? `Fix this first: ${draftBlockingIssue.message}`
                        : `Apply (${modKey()}+↵)`
                  }
                  aria-keyshortcuts={`${modKey()}+Enter`}
                >
                  Apply
                  <kbd className="editor-draft-kbd">{modKey()} ↵</kbd>
                </button>
              </div>
            )}
          </div>
          {view.description && <p className="editor-description">{view.description}</p>}
        </header>
        <div className="editor-body">
          <NodeRenderer view={view} />
        </div>
      </div>
    </ZoomContext.Provider>
  );
}

function NodeRenderer({ view }: { view: ViewNode }): JSX.Element {
  switch (view.kind) {
    case 'object':
      return <ObjectRenderer view={view} />;
    case 'array':
      return <ArrayRenderer view={view} />;
    case 'oneOf':
      return <OneOfRenderer view={view} />;
    case 'string':
    case 'number':
    case 'boolean':
    case 'enum':
      return <LeafWrapper view={view} />;
    case 'unsupported':
      return (
        <div className="editor-unsupported" role="alert">
          <strong>Can't render this node.</strong>
          <p>{view.reason}</p>
        </div>
      );
  }
}

/* ---------------- object ---------------- */

function ObjectRenderer({ view }: { view: ObjectView }): JSX.Element {
  const store = useStore();
  const state = useAppState();
  const inlineDepth = useContext(InlineDepthContext);
  const [addOpen, setAddOpen] = useState(false);

  const handleAddConfirm = (key: string, typeChoice: string | undefined): void => {
    const aps = view.additionalPropertySchema;
    if (!aps) return;
    let fresh: JsonValue;
    let resolvedTarget: JsonSchema | undefined;
    if (typeChoice !== undefined) {
      // Resolve $ref before reading variants — otherwise a meta-schema-style
      // `{ $ref: '#/$defs/Schema' }` would look like a non-union and we'd
      // build the default for the wrong shape.
      const resolvedAps = resolveSchema(aps, state.schema);
      const variants = resolvedAps.oneOf ?? resolvedAps.anyOf ?? [];
      const variant = variants.find((v) => {
        // Variants in the Schema def are themselves $refs (StringSchema,
        // ObjectSchema, etc.). Resolve before reading.
        const resolved = resolveSchema(v, state.schema);
        const t = resolved.properties?.type?.const;
        if (typeof t === 'string') return t === typeChoice;
        return resolved.title === typeChoice;
      });
      resolvedTarget = variant
        ? resolveSchema(variant, state.schema)
        : resolveSchema(aps, state.schema);
      fresh = defaultValueFor(resolvedTarget, state.schema);
    } else {
      resolvedTarget = resolveSchema(aps, state.schema);
      fresh = defaultValueFor(resolvedTarget, state.schema);
    }
    setAddOpen(false);

    const wouldBeInline = resolvedTarget?.['x-bliksys']?.layout === 'inline';
    if (wouldBeInline) {
      // Inline additions stay put — the inline row already has its own
      // header controls, and zooming would be jarring when the editor
      // is right there in front of the user. No draft scope is needed
      // either; the user can edit the new field directly. setValue
      // commits straight to the canonical state (no draft active).
      const newPointer = appendPointer(view.pointer, key);
      store.setValue(newPointer, fresh);
      return;
    }
    // Zoom-style addition: snapshot first, then add. beginCreate does
    // both atomically so Discard can restore the pre-add state.
    store.beginCreate((data) => {
      const newPointer = appendPointer(view.pointer, key);
      const next = setAtPointer(data, newPointer, fresh);
      return { next, pointer: newPointer };
    });
  };

  // The schema-declared (i.e. non-additional) fields are what we group.
  // Additional-property children are rendered separately below in the
  // DataTable / inline-field passes — they're authored by the user, not
  // by the schema, so semantic grouping doesn't apply there.
  const groupableFields = view.fields.filter((f) => !f.isAdditional || f.layout === 'inline');

  return (
    <div className="object-fields">
      {view.fields.length === 0 && (
        <p className="empty-hint">
          {view.additionalPropertySchema
            ? 'No fields yet. Add one to get started.'
            : 'This object has no editable fields.'}
        </p>
      )}
      {/*
       * Pass 1 — schema-declared fields. These have a fixed shape per
       * the schema and use the inline-expand / zoom-row / inline-field
       * choices. We deliberately filter out additional-property fields
       * here even when the schema mixes both, because they're owned by
       * the user and follow a different interaction model below.
       *
       * BUT: additionalProperty fields with `layout: 'inline'` keep
       * the InlineFieldRow path — they're the schema editor's own
       * property-map children, which need rename + inline-expand body
       * + per-row controls. The DataTable path is for content-side
       * record entries (no rename, click-to-zoom).
       *
       * When the schema declares `x-bliksys.fieldGroups`, render those
       * fields under labeled sections. Otherwise, fall through to the
       * flat list (the long-standing default).
       */}
      {view.fieldGroups ? (
        <GroupedFields
          parentPointer={view.pointer}
          fields={groupableFields}
          groups={view.fieldGroups}
          ungrouped={view.ungroupedFields}
          inlineDepth={inlineDepth}
        />
      ) : (
        renderFieldRows(groupableFields, view.pointer, inlineDepth)
      )}

      {/*
       * Pass 2 — additionalProperty children that aren't inline-expanded.
       * These are the content-editor's "Constrain to schema" record
       * entries. Rendered as a DataTable: each user-added entry has
       * the same affordances as an array item — click to zoom in,
       * attached delete, drag-handle reorder. No rename here; the user
       * picked the key when adding the field, and a record's keys are
       * stable identifiers, not editable in flight.
       */}
      {view.additionalPropertySchema && (
        <AdditionalPropertiesList
          fields={view.fields.filter((f) => f.isAdditional && f.layout !== 'inline')}
          parentPointer={view.pointer}
        />
      )}

      {view.additionalPropertySchema && (
        <button type="button" className="array-add" onClick={() => setAddOpen(true)}>
          + Add field
        </button>
      )}
      {addOpen && view.additionalPropertySchema && (
        <AddFieldDialog
          existingKeys={view.fields.map((f) => f.key)}
          additionalPropsSchema={view.additionalPropertySchema}
          rootSchema={state.schema}
          onConfirm={handleAddConfirm}
          onCancel={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Render a list of object fields as the existing flat sequence — no
 * sectioning, no group headers. Pulled out as its own helper so the
 * grouped path can render each section's slice through the same code,
 * keeping the inline-expand / zoom / inline-field mode selection
 * authoritative in one place.
 *
 * The reorder-context (prev/next/afterNext keys) is computed against
 * the list passed in, NOT the full ObjectView. That's intentional: when
 * fields are grouped, reorder lives within the group, not across it.
 * For schemas with `propOrder` (the typical case for grouped variants)
 * the order is fixed anyway, so this difference is rarely observable.
 */
function renderFieldRows(
  fields: readonly FieldDescriptor[],
  parentPointer: string,
  inlineDepth: number,
): React.ReactNode {
  return fields.map((field, idx, arr) => {
    const useInlineExpand =
      field.zoomable && field.layout === 'inline' && inlineDepth < MAX_INLINE_DEPTH;
    if (useInlineExpand) {
      return (
        <InlineFieldRow
          key={field.key}
          field={field}
          parentPointer={parentPointer}
          prevKey={idx > 0 ? arr[idx - 1]?.key : undefined}
          nextKey={idx < arr.length - 1 ? arr[idx + 1]?.key : undefined}
          afterNextKey={idx < arr.length - 2 ? arr[idx + 2]?.key : undefined}
        />
      );
    }
    return (
      <FieldRow key={field.key} field={field}>
        {field.zoomable ? <ZoomRow field={field} /> : <InlineField field={field} />}
      </FieldRow>
    );
  });
}

/**
 * Sectioned rendering for object schemas with `x-bliksys.fieldGroups`.
 * Each group becomes a `<details>` element with the group title in the
 * `<summary>` and the section description below it. Groups marked
 * `collapsedByDefault: true` start closed UNLESS at least one field in
 * the group has a non-default value — the user shouldn't have to hunt
 * for data they've already entered.
 *
 * Fields not declared in any group fall under an implicit "Other"
 * section at the end so nothing disappears (covers extension fields and
 * authoring oversights). When `ungroupedFields` is empty, the section
 * is skipped entirely.
 */
function GroupedFields({
  parentPointer,
  fields,
  groups,
  ungrouped,
  inlineDepth,
}: {
  parentPointer: string;
  fields: readonly FieldDescriptor[];
  groups: readonly ObjectFieldGroup[];
  ungrouped: readonly string[];
  inlineDepth: number;
}): JSX.Element {
  const byKey = new Map(fields.map((f) => [f.key, f] as const));
  return (
    <>
      {groups.map((group) => {
        const groupFields = group.fields
          .map((k) => byKey.get(k))
          .filter((f): f is FieldDescriptor => f !== undefined);
        // If the group is empty (e.g. all listed fields are absent in this
        // variant), skip the section entirely. Showing an empty header
        // adds noise without information.
        if (groupFields.length === 0) return null;
        return (
          <FieldGroupSection
            key={group.id}
            group={group}
            fields={groupFields}
            parentPointer={parentPointer}
            inlineDepth={inlineDepth}
          />
        );
      })}
      {ungrouped.length > 0 && (
        <FieldGroupSection
          group={{
            id: '__other__',
            title: 'Other',
            description:
              'Fields not assigned to a section. Typically extension hints or schema metadata.',
            fields: ungrouped,
            collapsedByDefault: true,
          }}
          fields={ungrouped
            .map((k) => byKey.get(k))
            .filter((f): f is FieldDescriptor => f !== undefined)}
          parentPointer={parentPointer}
          inlineDepth={inlineDepth}
        />
      )}
    </>
  );
}

/**
 * One section of grouped fields. Rendered as a `<details>` with the
 * group title in the summary and a one-paragraph "what this section is
 * for" description below it. The auto-expand-on-changes behavior is the
 * key UX promise: a user who's already filled in a Composition rule
 * will see it on every reload, not have to reopen the section first.
 */
function FieldGroupSection({
  group,
  fields,
  parentPointer,
  inlineDepth,
}: {
  group: ObjectFieldGroup;
  fields: readonly FieldDescriptor[];
  parentPointer: string;
  inlineDepth: number;
}): JSX.Element {
  // Auto-expand if anything in the section is set. `preview` is what the
  // ZoomRow already computes for "is this empty"; an empty array is
  // "[]", an empty object is "{}", an empty string is "" — we treat all
  // of those as "no value yet". Anything else means the user has
  // touched the field.
  const hasValue = fields.some((f) => isMeaningfulPreview(f.preview));
  const initiallyOpen = !group.collapsedByDefault || hasValue;
  return (
    <details className="field-group" open={initiallyOpen}>
      <summary className="field-group-summary">
        <span className="field-group-title">{group.title}</span>
        <span className="field-group-count" aria-label={`${fields.length} fields`}>
          {fields.length}
        </span>
      </summary>
      {group.description && <p className="field-group-description">{group.description}</p>}
      <div className="field-group-body">{renderFieldRows(fields, parentPointer, inlineDepth)}</div>
    </details>
  );
}

/**
 * "Has the user entered something?" heuristic. Empty containers and the
 * empty string register as no-value; everything else is meaningful.
 * Used by the section auto-expand logic.
 */
export function isMeaningfulPreview(preview: string): boolean {
  if (preview === '' || preview === '[]' || preview === '{}') return false;
  return !(preview === 'null' || preview === 'undefined');
}

/**
 * DataTable view of additionalProperty children. Each user-added key
 * is a row: leading column shows the key name, body shows the value
 * preview, click-to-zoom navigates in, the attached × deletes.
 *
 * Reorder via drag is enabled — users authoring a record-map (e.g.
 * a list of variants keyed by name) generally want to control the
 * iteration order. The `moveObjectKey` store op handles the rename
 * dance internally.
 */
function AdditionalPropertiesList({
  fields,
  parentPointer,
}: {
  fields: readonly FieldDescriptor[];
  parentPointer: string;
}): JSX.Element | null {
  const store = useStore();
  const zoomInto = useContext(ZoomContext);
  if (fields.length === 0) return null;

  const rows = fields.map((field) => ({
    id: field.key,
    leading: <span className="addl-row-key">{field.key}</span>,
    body: (
      <div className="zoom-row-content">
        <span className="zoom-row-summary">{field.preview || 'Empty'}</span>
        <span className="zoom-row-chevron" aria-hidden="true">
          ›
        </span>
      </div>
    ),
    onClick: () => zoomInto(field.pointer),
  }));

  return (
    <DataTable
      rows={rows}
      onReorder={(from, to) => {
        // Build the move using before-key semantics that store.moveObjectKey
        // expects: pass the key currently at `to` (or a key past `to` when
        // moving downward) so the moved key lands in front of it.
        const fromKey = fields[from]?.key;
        if (fromKey === undefined) return;
        // Drop position interpretation: if the user dragged onto an index,
        // they want the moved row to occupy THAT slot. moveObjectKey takes
        // the key that should end up immediately AFTER the moved key.
        // When `to` is past the end (moving down to last position), pass
        // null = "to the end".
        const beforeKey = to >= fields.length - 1 ? null : (fields[to + 1]?.key ?? null);
        // Special case: moving up — the row at `to` should end up after
        // the moved row, so beforeKey IS that row's key.
        const beforeKeyEffective = from > to ? (fields[to]?.key ?? null) : beforeKey;
        store.moveObjectKey(parentPointer, fromKey, beforeKeyEffective);
      }}
      onDelete={(index) => {
        const target = fields[index];
        if (target) store.deleteValue(target.pointer);
      }}
      ariaLabel="Additional properties"
    />
  );
}

/**
 * Row wrapper that adds a delete affordance to user-added (additional)
 * fields. Properties declared in the schema's `properties` map are
 * considered structural and aren't deleted from the row level — to
 * remove those, the developer edits the schema.
 */
/**
 * FieldRow's sibling for inline-expanded complex fields. Owns the
 * rename + delete controls, which appear in the section header (next
 * to the label) rather than as a × at the row's right edge. Rename
 * uses the store's atomic `renameKey` so configured constraints are
 * preserved.
 */
function InlineFieldRow({
  field,
  parentPointer,
  prevKey,
  nextKey,
  afterNextKey,
}: {
  field: FieldDescriptor;
  parentPointer: string;
  /** Key immediately before `field` in display order, undefined if first. */
  prevKey: string | undefined;
  /** Key immediately after `field`, undefined if last. */
  nextKey: string | undefined;
  /** Key two positions after `field`, used to compute the beforeKey
   * argument when moving down. Undefined when there's no such key
   * (move-down means "to the end"). */
  afterNextKey: string | undefined;
}): JSX.Element {
  const store = useStore();
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(field.key);
  const inputRef = useRef<HTMLInputElement>(null);

  // When entering rename mode, focus the input + select all so the user
  // can immediately type a replacement. Avoids `autoFocus` (which is an
  // a11y anti-pattern because it steals focus on render).
  useEffect(() => {
    if (renaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [renaming]);

  const handleDelete = (): void => {
    void confirm({
      title: `Delete field "${field.key}"?`,
      message: 'This removes the property and any value configured for it.',
      confirmLabel: 'Delete',
      danger: true,
    }).then((ok) => {
      if (ok) store.deleteValue(field.pointer);
    });
  };

  const handleMoveUp = (): void => {
    if (prevKey === undefined) return;
    // Move `field.key` to the position before `prevKey` — that is, swap them.
    // reorderSchemaProperty also writes x-bliksys.propOrder on the
    // grandparent schema when parentPointer is a properties map, so
    // the order survives serialization through alphabetic-sorting
    // JSON encoders.
    store.reorderSchemaProperty(parentPointer, field.key, prevKey);
  };

  const handleMoveDown = (): void => {
    if (nextKey === undefined) return;
    // Move past `nextKey`. afterNextKey is null if `nextKey` is last,
    // meaning "to the end".
    store.reorderSchemaProperty(parentPointer, field.key, afterNextKey ?? null);
  };

  const beginRename = (): void => {
    setDraft(field.key);
    setRenaming(true);
  };

  const commitRename = (): void => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === field.key) {
      setRenaming(false);
      return;
    }
    if (trimmed.includes('/') || trimmed.includes('~')) {
      window.alert('Field names can\'t contain "/" or "~".');
      return;
    }
    // Build the new pointer: same parent, new last segment.
    const parts = field.pointer.split('/');
    parts[parts.length - 1] = trimmed.replace(/~/g, '~0').replace(/\//g, '~1');
    const newPointer = parts.join('/');
    store.renameKey(field.pointer, newPointer);
    setRenaming(false);
  };

  // Only `isAdditional` fields support rename + delete — these are
  // user-added properties. Schema-declared properties stay as-is.
  if (!field.isAdditional) {
    return <InlineExpandedField field={field} />;
  }

  const headerSlot = renaming ? (
    <div className="inline-expand-rename">
      <input
        ref={inputRef}
        type="text"
        className="input inline-expand-rename-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitRename();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setRenaming(false);
          }
        }}
        onBlur={commitRename}
      />
    </div>
  ) : (
    <>
      <button
        type="button"
        className="inline-expand-action"
        onClick={handleMoveUp}
        disabled={prevKey === undefined}
        title="Move up"
        aria-label={`Move ${field.key} up`}
      >
        ▲
      </button>
      <button
        type="button"
        className="inline-expand-action"
        onClick={handleMoveDown}
        disabled={nextKey === undefined}
        title="Move down"
        aria-label={`Move ${field.key} down`}
      >
        ▼
      </button>
      <button
        type="button"
        className="inline-expand-action"
        onClick={beginRename}
        title="Rename property"
        aria-label={`Rename property ${field.key}`}
      >
        Rename
      </button>
      <button
        type="button"
        className="inline-expand-action inline-expand-action-danger"
        onClick={handleDelete}
        title="Delete property"
        aria-label={`Delete property ${field.key}`}
      >
        Delete
      </button>
    </>
  );

  return <InlineExpandedField field={field} headerSlot={headerSlot} />;
}

function FieldRow({
  field,
  children,
}: {
  field: FieldDescriptor;
  children: React.ReactNode;
}): JSX.Element {
  const store = useStore();
  if (!field.isAdditional) return <>{children}</>;
  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    void confirm({
      title: `Delete field "${field.key}"?`,
      message: 'This removes the property and any value configured for it.',
      confirmLabel: 'Delete',
      danger: true,
    }).then((ok) => {
      if (ok) store.deleteValue(field.pointer);
    });
  };
  return (
    <div className="field-row-with-delete">
      <div className="field-row-content">{children}</div>
      <button
        type="button"
        className="field-row-delete"
        aria-label={`Delete field ${field.key}`}
        onClick={handleDelete}
      >
        ×
      </button>
    </div>
  );
}

/**
 * Inline-expanded view of a complex field: instead of rendering a
 * zoom-row that navigates on click, we render the nested editor right
 * here. Used for `properties` and `items` in the meta-schema (and any
 * other field opted in via `x-bliksys.layout: 'inline'`).
 *
 * Visual treatment is a labeled section. The header shows the label;
 * the body is the recursive NodeRenderer. Header description sits
 * above the body for context. Errors propagate as normal because the
 * nested view's pointer matches the engine's view tree.
 */
function InlineExpandedField({
  field,
  headerSlot,
}: {
  field: FieldDescriptor;
  /**
   * Optional content rendered to the right of the label in the header.
   * The FieldRow uses this to put rename / delete controls inline with
   * the section title.
   */
  headerSlot?: React.ReactNode;
}): JSX.Element {
  const state = useAppState();
  const nestedView = useMemo(
    () =>
      buildView(field.pointer, {
        rootSchema: state.schema,
        rootData: state.data,
        refRegistry: state.refRegistry,
      }),
    [field.pointer, state.schema, state.data, state.refRegistry],
  );

  const inlineDepth = useContext(InlineDepthContext);

  return (
    <section className="inline-expand">
      <header className="inline-expand-header">
        <div className="inline-expand-title-row">
          <div className="inline-expand-label">
            {field.label}
            {field.required && (
              <span className="field-label-required" aria-label="required">
                ●
              </span>
            )}
          </div>
          {headerSlot && <div className="inline-expand-actions">{headerSlot}</div>}
        </div>
        {field.description && <p className="inline-expand-description">{field.description}</p>}
      </header>
      <div className="inline-expand-body">
        <InlineDepthContext.Provider value={inlineDepth + 1}>
          <NodeRenderer view={nestedView} />
        </InlineDepthContext.Provider>
      </div>
    </section>
  );
}

function ZoomRow({ field }: { field: FieldDescriptor }): JSX.Element {
  const zoomInto = useContext(ZoomContext);
  return (
    <button type="button" className="zoom-row" onClick={() => zoomInto(field.pointer)}>
      <div className="zoom-row-main">
        <div className="zoom-row-label">
          {field.label}
          {field.required && (
            <span className="field-label-required" aria-label="required">
              ●
            </span>
          )}
        </div>
        {field.description && <div className="zoom-row-description">{field.description}</div>}
        <div className="zoom-row-preview">{field.preview || 'Empty'}</div>
      </div>
      <span className="zoom-row-chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

function InlineField({ field }: { field: FieldDescriptor }): JSX.Element {
  const state = useAppState();
  // Build the child view in-place. Cheap: schemaAtPointer is already memoized
  // transitively by React re-rendering only when state.pointer/data changes.
  const childView = useMemo(
    () =>
      buildView(field.pointer, {
        rootSchema: state.schema,
        rootData: state.data,
        refRegistry: state.refRegistry,
      }),
    [field.pointer, state.schema, state.data, state.refRegistry],
  );

  // Scalar arrays are non-zoomable but not leaves — they render inline inside
  // their parent object's field card. Use the array renderer, wrapped in the
  // same label/card shell the leaf fields get, so the UI reads consistently.
  if (childView.kind === 'array') {
    return (
      <div className="leaf-field">
        <FieldLabel
          label={childView.label}
          required={childView.required}
          description={childView.description}
        />
        <ArrayRenderer view={childView} />
      </div>
    );
  }

  return <LeafWrapper view={childView} />;
}

/* ---------------- leaf wrapper (label + editor + error) ---------------- */

function LeafWrapper({ view }: { view: ViewNode }): JSX.Element {
  const store = useStore();
  const state = useAppState();
  const id = useId();
  const issueForPath = findIssueFor(view.pointer, state.issues);

  const set = (v: unknown): void => {
    // For optional leaves, treat clearing the input as deleting the
    // value rather than storing an empty string / NaN. This matters for
    // schema-author fields like `default` where storing `""` against a
    // schema with `enum: ['a','b']` would trigger a "default not in
    // enum" warning the user can't fix without removing the field
    // entirely.
    if (!view.required) {
      if (v === '' || v === undefined) {
        store.deleteValue(view.pointer);
        return;
      }
    }
    store.setValue(view.pointer, v as never);
  };

  let editor: JSX.Element;
  switch (view.kind) {
    case 'string': {
      // When the schema declares an autocomplete source via
      // x-bliksys.refsFrom, resolve it against the live document and
      // hand the input a list of `#<pointer>/<key>` suggestions. The
      // canonical case is the meta-schema's $ref field with
      // refsFrom: '/$defs' — produces `#/$defs/MyType` autocomplete
      // entries pulled from whatever `$defs` map the user has at the
      // schema root.
      //
      // discriminatorCandidatesFrom drives the same datalist via a
      // different source: the parent oneOf's variants. Both can
      // contribute simultaneously in principle; in practice each
      // string field uses at most one.
      const refOptions = view.refsFrom ? refOptionsFor(view.refsFrom, state.data) : undefined;
      const discCandidates = view.discriminatorCandidatesFrom
        ? discriminatorCandidatesFor(view.discriminatorCandidatesFrom, view.pointer, state.data)
        : undefined;
      const merged =
        refOptions !== undefined || discCandidates !== undefined
          ? [...(refOptions ?? []), ...(discCandidates ?? [])]
          : undefined;
      editor = <StringInput view={view} id={id} onChange={set} refOptions={merged} />;
      break;
    }
    case 'number':
      editor = <NumberInput view={view} id={id} onChange={set} />;
      break;
    case 'boolean':
      editor = <BooleanInput view={view} id={id} onChange={set} />;
      break;
    case 'enum':
      editor = <EnumInput view={view} id={id} onChange={set} />;
      break;
    default:
      editor = <em>Not an editable leaf.</em>;
  }

  const description = composeDescription(view);

  return (
    <div className="leaf-field" data-leaf-kind={view.kind}>
      <FieldLabel
        htmlFor={id}
        label={view.label}
        required={view.required}
        description={description}
        error={issueForPath}
        kind={
          view.kind === 'string' ||
          view.kind === 'number' ||
          view.kind === 'boolean' ||
          view.kind === 'enum'
            ? view.kind
            : undefined
        }
      />
      {editor}
    </div>
  );
}

/**
 * Build a field description that includes constraint hints alongside any
 * schema-provided description. Gives users something concrete to check
 * against when their input is rejected.
 */
function composeDescription(view: ViewNode): string | undefined {
  const parts: string[] = [];
  if (view.description) parts.push(view.description);
  if (view.kind === 'string') {
    if (view.acceptedFormats && view.acceptedFormats.length > 0) {
      parts.push(`Accepts: ${view.acceptedFormats.map(humanFormat).join(', ')}.`);
    } else if (view.format) {
      parts.push(`Format: ${humanFormat(view.format)}.`);
    }
    if (view.pattern) parts.push(`Must match ${view.pattern}`);
    if (view.minLength !== undefined && view.maxLength !== undefined) {
      parts.push(`${view.minLength}–${view.maxLength} characters.`);
    } else if (view.minLength !== undefined) {
      parts.push(`At least ${view.minLength} characters.`);
    } else if (view.maxLength !== undefined) {
      parts.push(`At most ${view.maxLength} characters.`);
    }
  } else if (view.kind === 'number') {
    if (view.minimum !== undefined && view.maximum !== undefined) {
      parts.push(`Between ${view.minimum} and ${view.maximum}.`);
    } else if (view.minimum !== undefined) {
      parts.push(`Minimum ${view.minimum}.`);
    } else if (view.maximum !== undefined) {
      parts.push(`Maximum ${view.maximum}.`);
    }
    if (view.multipleOf !== undefined) parts.push(`Multiple of ${view.multipleOf}.`);
  }
  return parts.length > 0 ? parts.join(' ') : undefined;
}

const FORMAT_LABELS: Record<string, string> = {
  hostname: 'hostname',
  ipv4: 'IPv4 address',
  ipv6: 'IPv6 address',
  email: 'email',
  uri: 'URL',
  uuid: 'UUID',
  date: 'date',
  'date-time': 'date and time',
};

function humanFormat(f: string): string {
  return FORMAT_LABELS[f] ?? f;
}

/* ---------------- array ---------------- */

function ArrayRenderer({ view }: { view: ArrayView }): JSX.Element {
  const store = useStore();
  const state = useAppState();
  const canAdd = view.maxItems === undefined || view.items.length < view.maxItems;

  const handleAdd = (): void => {
    const fresh = defaultValueFor(view.itemSchema, state.schema);
    if (view.itemsAreScalar) {
      // Scalars: add inline, no draft. The user edits the chip in
      // place; there's no zoom-in step where Apply/Discard would help.
      const newItemPointer = appendPointer(view.pointer, view.items.length);
      store.setValue(newItemPointer, fresh);
      return;
    }
    // Zoomable items: snapshot, add, navigate — atomically. Discard
    // rolls back to the pre-add state, so an in-progress invalid item
    // never leaks into the canonical (committed) state that
    // download/deploy/save consume.
    store.beginCreate((data) => {
      const newItemPointer = appendPointer(view.pointer, view.items.length);
      const next = setAtPointer(data, newItemPointer, fresh);
      return { next, pointer: newItemPointer };
    });
  };

  // The select widget is a complete substitute — no add button, no
  // empty-hint, no scalar chip editor. The user picks from a fixed set
  // of options pulled from a sibling location in the data.
  if (view.widget === 'select') {
    return <SelectFromSiblingArray view={view} />;
  }

  return (
    <div className="array-list">
      {view.items.length === 0 && (
        <p className="empty-hint">No items yet. Add one to get started.</p>
      )}
      {view.itemsAreScalar ? <ScalarArrayEditor view={view} /> : <ZoomableArrayList view={view} />}
      {canAdd && (
        <button type="button" className="array-add" onClick={handleAdd}>
          + Add {singularize(view.label)}
        </button>
      )}
      {view.minItems !== undefined && view.items.length < view.minItems && (
        <p className="array-hint" role="alert">
          Must have at least {view.minItems} items.
        </p>
      )}
    </div>
  );
}

/**
 * Multi-select rendering for string arrays whose options come from a
 * sibling location in the data. Used by the meta-schema's `required`
 * field — its `optionsFromSibling: '../properties'` makes the available
 * options the property names defined in the same object schema.
 *
 * Resolves the sibling pointer, reads the keys (object) or values
 * (string array), and renders a checkbox per option. Toggling rewrites
 * the array.
 */
function SelectFromSiblingArray({ view }: { view: ArrayView }): JSX.Element {
  const state = useAppState();
  const store = useStore();

  const options = useMemo(() => {
    if (!view.optionsFromSibling) return [];
    const targetPointer = resolveRelativePointer(view.pointer, view.optionsFromSibling);
    if (targetPointer === null) return [];
    const sibling = readAtPointer(state.data, targetPointer);
    if (sibling === undefined || sibling === null) return [];
    if (Array.isArray(sibling)) {
      return sibling.filter((v): v is string => typeof v === 'string');
    }
    if (typeof sibling === 'object') {
      return Object.keys(sibling);
    }
    return [];
  }, [view.optionsFromSibling, view.pointer, state.data]);

  const selected = useMemo(() => {
    // The current array values, normalized to strings.
    const arr = readAtPointer(state.data, view.pointer);
    if (!Array.isArray(arr)) return new Set<string>();
    return new Set(arr.filter((v): v is string => typeof v === 'string'));
  }, [state.data, view.pointer]);

  const toggle = (option: string): void => {
    const next = new Set(selected);
    if (next.has(option)) next.delete(option);
    else next.add(option);
    // Preserve option order (sibling-order is more meaningful than
    // insertion-order).
    const arr = options.filter((o) => next.has(o));
    store.setValue(view.pointer, arr);
  };

  if (options.length === 0) {
    return <p className="empty-hint">No options yet — add a property first to mark it required.</p>;
  }

  return (
    <div className="select-sibling-list" role="group" aria-label={view.label}>
      {options.map((option) => {
        const id = `${view.pointer}-${option}`;
        return (
          <label key={option} htmlFor={id} className="select-sibling-item">
            <input
              id={id}
              type="checkbox"
              checked={selected.has(option)}
              onChange={() => toggle(option)}
            />
            <span>{option}</span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * Resolve a sibling-relative pointer (`../properties`, `../../foo`)
 * against a base pointer. Returns null if the relative pointer escapes
 * the document root.
 */
function resolveRelativePointer(base: string, rel: string): string | null {
  // Split off the up-segments (each `..` strips one segment from base).
  let upCount = 0;
  let remainder = rel;
  while (remainder.startsWith('../')) {
    upCount++;
    remainder = remainder.slice(3);
  }
  if (remainder.startsWith('..')) {
    upCount++;
    remainder = remainder.slice(2);
  }
  const baseSegments = base.split('/').filter((s) => s !== '');
  if (upCount > baseSegments.length) return null;
  const remaining = baseSegments.slice(0, baseSegments.length - upCount);
  const tail = remainder.startsWith('/') ? remainder.slice(1) : remainder;
  const tailSegments = tail.length > 0 ? tail.split('/') : [];
  const all = [...remaining, ...tailSegments];
  return all.length === 0 ? '' : '/' + all.join('/');
}

/**
 * Read a JSON value at a JSON-pointer path. Returns undefined for any
 * traversal failure.
 */
function readAtPointer(root: JsonValue, pointer: string): JsonValue | undefined {
  if (pointer === '') return root;
  const segments = pointer
    .split('/')
    .slice(1)
    .map((s) => s.replace(/~1/g, '/').replace(/~0/g, '~'));
  let cur: JsonValue | undefined = root;
  for (const seg of segments) {
    if (cur === null || cur === undefined) return undefined;
    if (Array.isArray(cur)) {
      const i = Number(seg);
      if (!Number.isInteger(i)) return undefined;
      cur = cur[i];
    } else if (typeof cur === 'object') {
      cur = cur[seg];
    } else {
      return undefined;
    }
  }
  return cur;
}

/**
 * Zoom-row list for arrays of objects, unions, or nested arrays. Each row
 * opens its own editor page.
 */
function ZoomableArrayList({ view }: { view: ArrayView }): JSX.Element {
  const store = useStore();
  const zoomInto = useContext(ZoomContext);

  // Map ViewNode items to DataTable rows. The row body shows the
  // engine-computed summary (with the optional discriminator tag).
  // Click anywhere on the body zooms in. Drag handle and × button
  // are owned by DataTable.
  const rows = view.items.map((item) => ({
    id: String(item.index),
    leading: <span className="zoom-row-index">{item.index + 1}</span>,
    body: (
      <div className="zoom-row-content">
        <span className="zoom-row-summary">{item.summary}</span>
        {item.discriminatorValue && <span className="zoom-row-tag">{item.discriminatorValue}</span>}
        <span className="zoom-row-chevron" aria-hidden="true">
          ›
        </span>
      </div>
    ),
    onClick: () => zoomInto(item.pointer),
  }));

  return (
    <DataTable
      rows={rows}
      onReorder={(from, to) => store.moveArrayItem(view.pointer, from, to)}
      onDelete={(index) => {
        const target = view.items[index];
        if (target) store.deleteValue(target.pointer);
      }}
      ariaLabel={view.label}
    />
  );
}

/**
 * Inline editor for arrays of scalars. Each item is a small input + delete
 * button in the same row. No zooming — you see the whole list at once.
 *
 * We build a leaf view per item so the widget matches the item schema
 * (textarea for long strings, enum select, number input, etc.).
 */
function ScalarArrayEditor({ view }: { view: ArrayView }): JSX.Element {
  const store = useStore();

  // Each row's body is the inline editor for that item. The item's
  // pointer comes from the engine; we re-build the item view inside
  // ScalarArrayItem so widget choice (textarea/select/etc.) stays
  // engine-driven.
  const rows = view.items.map((item) => ({
    id: String(item.index),
    leading: <span className="zoom-row-index">{item.index + 1}</span>,
    body: <ScalarArrayItem pointer={item.pointer} index={item.index} />,
  }));

  return (
    <DataTable
      rows={rows}
      onReorder={(from, to) => store.moveArrayItem(view.pointer, from, to)}
      onDelete={(index) => {
        const target = view.items[index];
        if (target) store.deleteValue(target.pointer);
      }}
      ariaLabel={view.label}
    />
  );
}

/**
 * Inline editor for a single scalar-array item. Renders only the
 * editor — the row chrome (index, delete button, drag handle) is
 * owned by DataTable.
 */
function ScalarArrayItem({ pointer, index }: { pointer: string; index: number }): JSX.Element {
  const store = useStore();
  const state = useAppState();
  const itemView = useMemo(
    () =>
      buildView(pointer, {
        rootSchema: state.schema,
        rootData: state.data,
        refRegistry: state.refRegistry,
      }),
    [pointer, state.schema, state.data, state.refRegistry],
  );
  const id = useId();
  const set = (v: unknown): void => store.setValue(pointer, v as never);

  // `index` is forwarded for completeness — DataTable shows it in the
  // leading column, but it's still useful as an aria hint here.
  void index;

  switch (itemView.kind) {
    case 'string':
      return <StringInput view={itemView} id={id} onChange={set} />;
    case 'number':
      return <NumberInput view={itemView} id={id} onChange={set} />;
    case 'boolean':
      return <BooleanInput view={itemView} id={id} onChange={set} />;
    case 'enum':
      return <EnumInput view={itemView} id={id} onChange={set} />;
    case 'oneOf':
      // Item is a oneOf of scalars (the meta-schema's `enum`/`default`/
      // `const` use { oneOf: [{type:'string'},{type:'number'},...] }).
      // The user picks the type via a small inline switcher; the
      // editor below it adapts.
      return <ScalarArrayOneOfItem view={itemView} id={id} onChange={set} />;
    default:
      // Shouldn't happen — itemsAreScalar filter excludes complex types — but if a
      // schema slips through, render a clear diagnostic rather than crash.
      return <em className="empty-hint">Unsupported item type for inline editing.</em>;
  }
}

function singularize(label: string): string {
  if (label.endsWith('ies')) return label.slice(0, -3) + 'y';
  if (label.endsWith('s')) return label.slice(0, -1);
  return label;
}

/* ---------------- oneOf (tagged unions) ---------------- */

/**
 * Inline editor for a oneOf-of-primitives item in a scalar array. The
 * `enum`, `default`, and `const` fields in the meta-schema use this
 * pattern: each item is `{ oneOf: [{type:'string'}, {type:'number'},
 * {type:'boolean'}, {type:'null'}] }`. The user picks the type via a
 * small inline select; the editor below adapts.
 *
 * Picking a different type writes a fresh default value at the item's
 * pointer, replacing the current value — same semantics as switching
 * a oneOf variant at any other site.
 */
function ScalarArrayOneOfItem({
  view,
  id,
  onChange,
}: {
  view: OneOfView;
  id: string;
  onChange: (v: unknown) => void;
}): JSX.Element {
  const state = useAppState();
  const store = useStore();

  const handlePickVariant = (variantIndex: number): void => {
    const variant = view.variants[variantIndex];
    if (!variant) return;
    const fresh = defaultValueFor(variant.schema, state.schema);
    store.setValue(view.pointer, fresh);
  };

  const noneSelected = view.activeVariant === null || view.resolvedView === null;
  const resolved = view.resolvedView;

  let body: JSX.Element;
  if (noneSelected || !resolved) {
    body = <em className="empty-hint">Pick a type.</em>;
  } else {
    switch (resolved.kind) {
      case 'string':
        body = <StringInput view={resolved} id={id} onChange={onChange} />;
        break;
      case 'number':
        body = <NumberInput view={resolved} id={id} onChange={onChange} />;
        break;
      case 'boolean':
        body = <BooleanInput view={resolved} id={id} onChange={onChange} />;
        break;
      case 'enum':
        body = <EnumInput view={resolved} id={id} onChange={onChange} />;
        break;
      default:
        body = <em className="empty-hint">Unsupported nested type.</em>;
    }
  }

  return (
    <div className="scalar-array-oneof">
      <select
        className="input scalar-array-oneof-type"
        value={noneSelected ? '' : String(view.activeVariant)}
        aria-label="Item type"
        onChange={(e) => {
          if (e.target.value === '') return;
          handlePickVariant(Number(e.target.value));
        }}
      >
        {noneSelected && (
          <option value="" disabled>
            — type —
          </option>
        )}
        {view.variants.map((variant, i) => (
          <option key={i} value={i}>
            {variant.label}
          </option>
        ))}
      </select>
      <div className="scalar-array-oneof-body">{body}</div>
    </div>
  );
}

function OneOfRenderer({ view }: { view: OneOfView }): JSX.Element {
  const store = useStore();
  const state = useAppState();

  const handlePickVariant = (variantIndex: number): void => {
    const variant = view.variants[variantIndex];
    if (!variant) return;
    const fresh = defaultValueFor(variant.schema, state.schema);
    store.setValue(view.pointer, fresh);
  };

  const handleClear = (): void => {
    // Drop the value entirely — same as "no choice made". Used by the
    // Clear option in the type dropdown for non-locked unions where
    // "no value" is a meaningful state (e.g. additionalProperties:
    // omitted means "allowed by default").
    store.deleteValue(view.pointer);
  };

  const noneSelected = view.activeVariant === null || view.resolvedView === null;
  const showStaticType = view.discriminatorLocked && !noneSelected;
  const activeLabel =
    view.activeVariant !== null ? view.variants[view.activeVariant]?.label : undefined;

  // Grid picker — used when authors opt in via x-bliksys.variantPickerLayout
  // and no variant is selected yet. This is the prominent "what kind of
  // value is this?" choice on a fresh schema. Once a variant is picked
  // the grid steps aside; further switching uses the compact dropdown
  // (or the static label, if locked). For locked variants we never show
  // the grid even when noneSelected, because the lock implies "this was
  // chosen at creation, you can't change it now" — the empty state for
  // a locked union is already a contradiction we can't fix in UI.
  if (view.pickerLayout === 'grid' && noneSelected && !view.discriminatorLocked) {
    return (
      <div className="oneof-active">
        <VariantGridPicker
          variants={view.variants}
          activeVariant={view.activeVariant}
          onPick={handlePickVariant}
        />
      </div>
    );
  }

  return (
    <div className="oneof-active">
      <div className="oneof-switcher">
        <span className="oneof-switcher-label">Type</span>
        {showStaticType ? (
          <span className="oneof-static-type" title="Type can't be changed after creation">
            {activeLabel ?? '—'}
          </span>
        ) : (
          <select
            className="input"
            value={noneSelected ? '' : String(view.activeVariant)}
            aria-label="Choose type"
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') return;
              if (v === '__clear__') {
                handleClear();
                return;
              }
              handlePickVariant(Number(v));
            }}
          >
            {noneSelected && (
              <option value="" disabled>
                — pick a type —
              </option>
            )}
            {view.variants.map((variant, i) => (
              <option key={i} value={i}>
                {variant.label}
              </option>
            ))}
            {!noneSelected && <option value="__clear__">— clear —</option>}
          </select>
        )}
      </div>
      {noneSelected ? (
        <p className="empty-hint">Choose a type above to start editing.</p>
      ) : (
        // Skip rendering when the variant has nothing to edit (its value
        // is fully determined, e.g. enum: [true] for an "Allow" choice).
        // The user picks the variant from the type dropdown — that
        // selection IS the value. Showing a one-option enum below would
        // be redundant noise.
        !isFullyDetermined(view) && <NodeRenderer view={view.resolvedView!} />
      )}
    </div>
  );
}

/**
 * Tile-grid variant picker. Each variant is a button with the variant's
 * title, optional icon, and one-line description. A click commits the
 * default value for that variant via the parent's onPick — the existing
 * draft/commit pipeline takes it from there.
 *
 * Used when the parent oneOf opts in via `x-bliksys.variantPickerLayout:
 * 'grid'`. The canonical case is the schema editor's root type picker,
 * where "what kind of value does this field hold?" is the most
 * consequential choice an author makes — a grid of cards with semantic
 * iconography reads better than a `<select>` here.
 */
function VariantGridPicker({
  variants,
  activeVariant,
  onPick,
}: {
  variants: readonly OneOfView['variants'][number][];
  activeVariant: number | null;
  onPick: (index: number) => void;
}): JSX.Element {
  return (
    <div className="variant-grid" role="radiogroup" aria-label="Choose type">
      {variants.map((variant, i) => {
        const isActive = activeVariant === i;
        return (
          <button
            key={i}
            type="button"
            className={`variant-card${isActive ? ' is-active' : ''}`}
            role="radio"
            aria-checked={isActive}
            onClick={() => onPick(i)}
          >
            {variant.icon && (
              <span className="variant-card-icon" aria-hidden="true">
                <VariantIcon name={variant.icon} />
              </span>
            )}
            <span className="variant-card-title">{variant.label}</span>
            {variant.description && (
              <span className="variant-card-description">{variant.description}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Tiny mapping of `x-bliksys.variantIcon` strings to inline SVG glyphs.
 * Kept self-contained (no external icon library dependency) so a
 * single-file artifact-style deploy stays simple. The set covers what
 * the meta-schema declares; new icons get added here on demand.
 *
 * Glyphs are simplified Lucide-style strokes — the goal is "clearly
 * distinct silhouettes at 24px" rather than fidelity. An unknown name
 * falls back to a neutral square so a typo doesn't blow up the tile.
 */
function VariantIcon({ name }: { name: string }): JSX.Element {
  const stroke = 'currentColor';
  const props = {
    width: 24,
    height: 24,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'type': // String
      return (
        <svg {...props}>
          <polyline points="4 7 4 4 20 4 20 7" />
          <line x1="9" y1="20" x2="15" y2="20" />
          <line x1="12" y1="4" x2="12" y2="20" />
        </svg>
      );
    case 'hash': // Number
      return (
        <svg {...props}>
          <line x1="4" y1="9" x2="20" y2="9" />
          <line x1="4" y1="15" x2="20" y2="15" />
          <line x1="10" y1="3" x2="8" y2="21" />
          <line x1="16" y1="3" x2="14" y2="21" />
        </svg>
      );
    case 'binary': // Integer
      return (
        <svg {...props}>
          <rect x="14" y="14" width="6" height="6" rx="1" />
          <rect x="4" y="4" width="6" height="6" rx="1" />
          <path d="M4 14h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H4z" />
          <path d="M14 4h2v6h-2" />
          <path d="M14 7h4" />
        </svg>
      );
    case 'toggle-left': // Boolean
      return (
        <svg {...props}>
          <rect x="2" y="6" width="20" height="12" rx="6" />
          <circle cx="8" cy="12" r="3" fill={stroke} />
        </svg>
      );
    case 'circle-slash': // Null
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <line x1="5" y1="5" x2="19" y2="19" />
        </svg>
      );
    case 'braces': // Object
      return (
        <svg {...props}>
          <path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5a2 2 0 0 0 2 2h1" />
          <path d="M16 21h1a2 2 0 0 0 2-2v-5a2 2 0 0 1 2-2 2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1" />
        </svg>
      );
    case 'list': // Array
      return (
        <svg {...props}>
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      );
    case 'link': // Reference
      return (
        <svg {...props}>
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      );
    case 'split': // Union
      return (
        <svg {...props}>
          <path d="M16 3h5v5" />
          <path d="M8 3H3v5" />
          <path d="M21 3 14 10" />
          <path d="M3 3l7 7" />
          <path d="M12 13v8" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
        </svg>
      );
  }
}

/**
 * True when the active variant pins its value via `const` or a
 * single-element `enum`. Used to avoid rendering a useless one-option
 * picker beneath the type-switcher in three-state widgets.
 */
function isFullyDetermined(view: OneOfView): boolean {
  if (view.activeVariant === null) return false;
  const variant = view.variants[view.activeVariant];
  if (!variant) return false;
  const s = variant.schema;
  if (s.const !== undefined) return true;
  return Array.isArray(s.enum) && s.enum.length === 1;
}

/* ---------------- helpers ---------------- */

/**
 * Build a short summary of the immediate parent record so the user
 * knows what context they're editing inside. Returns `null` when:
 *  - we're at the root (no parent),
 *  - the parent isn't a meaningful container (string/number/null),
 *  - or we can't extract anything useful.
 *
 * Strategy:
 *  1. If parent value has a `x-bliksys.summaryField`, use it.
 *  2. Otherwise look for common naming keys: title, name, label, heading, id.
 *  3. Otherwise fall back to the parent's schema label (from x-bliksys or title).
 *  4. Otherwise null.
 */
/**
 * Compute the "Back to {name}" label. We walk up from the current
 * pointer one step at a time, skipping intermediate parents that
 * have no meaningful name of their own (a properties map's keys, an
 * items array's index, etc. — these are container plumbing, not
 * destinations the user thinks of as "places"). The first ancestor
 * whose value carries a title/name/label or whose schema declares
 * one wins.
 *
 * If we walk all the way to the root without finding anything, return
 * null and the Back button shows just "Back".
 */
/**
 * Compute the "Back to {name}" label AND target pointer. We walk up
 * from the current pointer one step at a time, skipping intermediate
 * parents that have no meaningful name of their own (a properties
 * map's keys, an items array's index, etc. — these are container
 * plumbing, not destinations the user thinks of as "places"). The
 * first ancestor whose value carries a title/name/label or whose
 * schema declares one wins.
 *
 * Returns BOTH the resolved pointer AND the label so the Back button
 * can navigate to the matching destination. Returning only the label
 * (and having Back step one level up) created a mismatch: Back said
 * "Site" but clicked-Back went to "Pages" first, forcing the user
 * to click Back twice.
 *
 * If we walk all the way to the root without finding anything, returns
 * `{ pointer: '', label: null }` so the caller can navigate to root
 * with a generic "Back" label.
 */
/**
 * Return `pointer` itself if its level is visible in the breadcrumb,
 * or the closest visible ancestor otherwise. Mirrors the breadcrumb's
 * skip-when-inline-expanded rule, so any place that programmatically
 * navigates after a multi-level operation (Apply, Discard) can land the
 * user on a view they could have reached via the UI. Without this, an
 * Apply at /pages/0 in the demo would zoom to /pages — a view the user
 * never explicitly visited and that the breadcrumb hides.
 */
export function visiblePointer(pointer: string, schema: JsonSchema, data: JsonValue): string {
  let p = pointer;
  while (p !== '') {
    let sub: JsonSchema | undefined;
    try {
      sub = schemaAtPointer(p, schema, data).schema;
    } catch {
      sub = undefined;
    }
    if (sub?.['x-bliksys']?.layout !== 'inline') return p;
    const par = parentPointer(p);
    if (par === null) return '';
    p = par;
  }
  return '';
}

export function computeBackTarget(
  pointer: string,
  schema: JsonSchema,
  data: JsonValue,
): { pointer: string; label: string | null } | null {
  if (pointer === '') return null;
  let currentParentP: string | null = parentPointer(pointer);
  while (currentParentP !== null) {
    if (currentParentP === '') {
      // Root reached — schema's own title is the root's identity.
      const label = schema.title ?? null;
      return { pointer: '', label };
    }
    let parentValue: JsonValue | undefined;
    try {
      parentValue = getAtPointer(data, currentParentP);
    } catch {
      currentParentP = parentPointer(currentParentP);
      continue;
    }
    let parentSchema: JsonSchema | undefined;
    try {
      parentSchema = schemaAtPointer(currentParentP, schema, data).schema;
    } catch {
      parentSchema = undefined;
    }
    // Inline-expanded ancestors are rendered IN their own parent's
    // view (the breadcrumb hides them when intermediate). The user
    // never explicitly visited them as a standalone view, so jumping
    // to them on Back/Esc would surface a page that's unreachable
    // from anywhere else in the UI. Walk past any such ancestor.
    if (parentSchema?.['x-bliksys']?.layout === 'inline') {
      currentParentP = parentPointer(currentParentP);
      continue;
    }
    if (
      parentValue !== undefined &&
      parentValue !== null &&
      typeof parentValue === 'object' &&
      !Array.isArray(parentValue)
    ) {
      // 1: explicit summary field
      const summaryKey = parentSchema?.['x-bliksys']?.summaryField;
      if (summaryKey && typeof parentValue[summaryKey] === 'string' && parentValue[summaryKey]) {
        return { pointer: currentParentP, label: parentValue[summaryKey] };
      }
      // 2: common naming keys
      for (const key of ['title', 'name', 'label', 'heading', 'id']) {
        const v = parentValue[key];
        if (typeof v === 'string' && v.length > 0) {
          return { pointer: currentParentP, label: v };
        }
      }
      // 3: tagged-union variant title. When the parent is a oneOf item
      // and none of the data-derived labels matched, the user is more
      // helped by "Heading" / "Image" than by walking past it. Match
      // breadcrumb logic — see `inferVariantTitleForBackTarget`.
      const variantTitle = inferVariantTitleForBackTarget(parentSchema, parentValue, schema);
      if (variantTitle) return { pointer: currentParentP, label: variantTitle };
      // 4: schema label fallback. Skip generic container labels like
      // "Properties" / "Items" — they're path artifacts, not real
      // destinations. Walk further up.
      if (parentSchema) {
        const label = parentSchema['x-bliksys']?.label ?? parentSchema.title;
        if (label && !isContainerLabel(label, currentParentP)) {
          return { pointer: currentParentP, label };
        }
      }
    }
    currentParentP = parentPointer(currentParentP);
  }
  return null;
}

/**
 * Backwards-compatible thin wrapper around `computeBackTarget` that
 * returns only the label. Kept for the existing tests; new callers
 * should use `computeBackTarget` directly so they can navigate to the
 * matching pointer.
 */
export function computeParentSummary(
  pointer: string,
  schema: JsonSchema,
  data: JsonValue,
): string | null {
  return computeBackTarget(pointer, schema, data)?.label ?? null;
}

/**
 * True for labels that name a path container rather than a real
 * destination. The properties map of an object schema is named
 * "Properties" — when the user is editing /properties/title, they
 * came from the object that owns it, not from "Properties" itself.
 * Detected from the trailing path segment too as a safety net.
 */
function isContainerLabel(label: string, pointer: string): boolean {
  const lastSegment = pointer.split('/').filter(Boolean).pop() ?? '';
  if (lastSegment === 'properties' || lastSegment === 'items' || lastSegment === '$defs') {
    return true;
  }
  // Generic words used by the meta-schema for container fields. Be
  // conservative — only filter out the exact strings.
  return label === 'Properties' || label === 'Items';
}

/**
 * Tagged-union fallback for the back-target label. When the parent
 * schema is a oneOf with a discriminator and the value carries the
 * discriminator's tag, return the matching variant's title — same
 * pattern as Breadcrumb.labelFor. Returns undefined when nothing
 * applies; caller continues to the schema-label fallback.
 *
 * Variants in real schemas are typically authored as $refs (e.g.
 * `{ $ref: '#/$defs/HeadingBlock' }`); we resolve them against the
 * root schema before inspecting the discriminator-pinning const.
 */
function inferVariantTitleForBackTarget(
  parentSchema: JsonSchema | undefined,
  parentValue: Record<string, JsonValue>,
  rootSchema: JsonSchema,
): string | undefined {
  if (!parentSchema?.oneOf || parentSchema.oneOf.length === 0) {
    return undefined;
  }
  const propName = parentSchema.discriminator?.propertyName ?? 'type';
  const tagValue = parentValue[propName];
  if (typeof tagValue !== 'string') return undefined;
  for (const wrapper of parentSchema.oneOf) {
    let variant: JsonSchema = wrapper;
    try {
      // Resolve local $refs so we can read the variant's own
      // discriminator-pinning const. resolveSchema is the engine helper
      // imported at the top of this file.
      variant = resolveSchema(wrapper, rootSchema);
    } catch {
      continue;
    }
    const constValue = variant.properties?.[propName]?.const;
    if (constValue === tagValue && variant.title) return variant.title;
  }
  return undefined;
}

function findIssueFor(
  pointer: string,
  issues: readonly { path: string; message: string }[],
): string | undefined {
  // Convert pointer to dotted path for comparison with validation output.
  // /pages/0/title -> pages[0].title
  const dotted = pointerToPath(pointer);
  return issues.find((i) => i.path === dotted)?.message;
}

function pointerToPath(pointer: string): string {
  if (pointer === '') return '';
  return pointer
    .slice(1)
    .split('/')
    .map((seg) => seg.replace(/~1/g, '/').replace(/~0/g, '~'))
    .reduce((acc, seg) => {
      if (/^\d+$/.test(seg)) return `${acc}[${seg}]`;
      return acc ? `${acc}.${seg}` : seg;
    }, '');
}

/**
 * Three-way prompt for navigation away from an active draft. The user
 * picks Apply (commit), Discard (roll back), or Cancel (stay put).
 *
 * Wording adapts to whether the draft is a creation or an edit, and
 * uses the editor's view label so the user knows what's at stake.
 */
async function confirmLeaveDraft(
  draftKind: 'create' | 'edit' | null,
  label: string,
): Promise<'apply' | 'discard' | 'cancel'> {
  const isCreate = draftKind === 'create';
  const choice = await confirmThreeWay({
    title: isCreate
      ? `New ${label.toLowerCase()} not yet applied`
      : `Unapplied changes to ${label.toLowerCase()}`,
    message: isCreate
      ? 'You can apply this new item to keep it, or discard it before navigating away.'
      : 'You can apply your edits to keep them, or discard them before navigating away.',
    primaryLabel: 'Apply',
    secondaryLabel: 'Discard',
    secondaryDanger: true,
  });
  if (choice === 'primary') return 'apply';
  if (choice === 'secondary') return 'discard';
  return 'cancel';
}

/**
 * Resolve an `x-bliksys.refsFrom` hint to a list of autocomplete
 * suggestions. Reads the data at the given absolute pointer and, if
 * it's an object, returns each key prefixed with `#<pointer>/`.
 *
 * Used by the meta-schema's Reference variant to surface available
 * `$defs` entries when authoring a `$ref`. Free-text entry is still
 * allowed in the input itself; this only populates the dropdown.
 *
 * Returns undefined when the target doesn't exist or isn't an object,
 * so the caller can decide not to render a datalist at all.
 */
function refOptionsFor(refsFrom: string, data: JsonValue): readonly string[] | undefined {
  let cursor: JsonValue = data;
  // refsFrom is an absolute JSON Pointer, e.g. '/$defs'. Walk it.
  const segments = refsFrom === '' ? [] : refsFrom.slice(1).split('/');
  for (const seg of segments) {
    if (cursor === null || typeof cursor !== 'object' || Array.isArray(cursor)) return undefined;
    const key = seg.replace(/~1/g, '/').replace(/~0/g, '~');
    cursor = cursor[key] as JsonValue;
    if (cursor === undefined) return undefined;
  }
  if (cursor === null || typeof cursor !== 'object' || Array.isArray(cursor)) return undefined;
  return Object.keys(cursor).map((k) => `#${refsFrom}/${k}`);
}

/**
 * Resolve a `discriminatorCandidatesFrom` hint against the document.
 * Walks every variant in the target oneOf array, follows a single level
 * of local `$ref` so $defs-based variants work, and returns the property
 * names pinned to a `const` across ALL variants. The result is the set
 * of legal discriminator names — exactly what the engine's
 * `inferDiscriminator` would accept.
 *
 * Returns undefined when the target isn't a non-empty oneOf array or no
 * candidate is shared by every variant. Free-text entry stays available
 * either way.
 */
function discriminatorCandidatesFor(
  rel: string,
  viewPointer: JsonPointer,
  data: JsonValue,
): readonly string[] | undefined {
  const oneOfPointer = resolveRelativePointer(viewPointer, rel);
  if (oneOfPointer === null) return undefined;
  const oneOf = readAtPointer(data, oneOfPointer);
  if (!Array.isArray(oneOf) || oneOf.length === 0) return undefined;

  const perVariant: Set<string>[] = [];
  for (const variant of oneOf) {
    const resolved = resolveLocalRef(variant, data);
    if (resolved === null) continue;
    const props = resolved.properties;
    if (
      props === undefined ||
      props === null ||
      typeof props !== 'object' ||
      Array.isArray(props)
    ) {
      continue;
    }
    const set = new Set<string>();
    for (const [key, propSchema] of Object.entries(props)) {
      if (propSchema === null || typeof propSchema !== 'object' || Array.isArray(propSchema)) {
        continue;
      }
      if ('const' in propSchema) set.add(key);
    }
    perVariant.push(set);
  }
  if (perVariant.length === 0) return undefined;
  const [first, ...rest] = perVariant;
  if (!first) return undefined;
  const intersection = [...first].filter((k) => rest.every((s) => s.has(k))).sort();
  return intersection.length > 0 ? intersection : undefined;
}

/**
 * Follow a single level of `{ $ref: '#/...' }` against the document.
 * Returns the variant itself when it isn't a ref. Returns null when the
 * input is not an object, the ref is non-local, or the target is missing.
 */
function resolveLocalRef(variant: JsonValue, data: JsonValue): Record<string, JsonValue> | null {
  if (variant === null || typeof variant !== 'object' || Array.isArray(variant)) return null;
  const ref = variant.$ref;
  if (typeof ref !== 'string') return variant;
  if (!ref.startsWith('#')) return null;
  const target = readAtPointer(data, ref.slice(1));
  if (target === null || typeof target !== 'object' || Array.isArray(target)) return null;
  return target;
}
